// How an intake form treats phone, number and currency boxes. Shared by the
// client link (IntakeFormFill), the admin preview (IntakeFormPreview) and the
// builder's preview (CreateIntakeForm). Plain DOM helpers, no React.
//
// A field's kind comes from the saved form: phones are `type: "tel"` (the
// built-in phone fields and Phone custom fields), numbers are `type: "number"`,
// and currency is a text box whose custom field type is "currency".
// Before OL-162 the built-in phones were saved as "number", so every number box
// was masked as a phone. The API now sends those old phones back as "tel".

const fieldName = (field) => field?.attrs?.name || field?.config?.sys?.full_name;

/** Map each field name on the form to its kind: "phone", "number" or "currency". */
export const getInputKinds = (schema) => {
    const kinds = {};
    const fields = Array.isArray(schema?.fields) ? schema.fields : Object.values(schema?.fields || {});

    fields.forEach((field) => {
        const name = fieldName(field);
        if (!name) return;

        const type = field?.attrs?.type;
        if (field?.config?.field_type === 'currency') kinds[name] = 'currency';
        else if (type === 'tel') kinds[name] = 'phone';
        else if (type === 'number') kinds[name] = 'number';
    });

    return kinds;
};

const formatUSPhone = (digits) => {
    const d = (digits || '').slice(0, 10);
    const p1 = d.slice(0, 3);
    const p2 = d.slice(3, 6);
    const p3 = d.slice(6, 10);

    if (!d) return '';
    if (d.length <= 3) return `(${p1}`;
    if (d.length <= 6) return `(${p1}) ${p2}`;
    return `(${p1}) ${p2}-${p3}`;
};

const formatE164 = (digits) => {
    const d = (digits || '').slice(0, 15);
    return d ? `+${d}` : '';
};

// If the user starts with "+", keep E.164, otherwise US format.
export const formatPhoneForDisplay = (raw) => {
    const s = String(raw ?? '').trim();
    const hasPlus = s.startsWith('+');
    const digits = s.replace(/\D/g, '');
    return hasPlus ? formatE164(digits) : formatUSPhone(digits);
};

/** Digits, one leading minus sign and one decimal point. */
export const sanitizeNumber = (raw) => {
    const s = String(raw ?? '').trim();
    const negative = s.startsWith('-');
    const [whole, ...decimals] = s.replace(/[^\d.]/g, '').split('.');
    const body = decimals.length ? `${whole}.${decimals.join('')}` : whole;
    return negative ? `-${body}` : body;
};

export const isValidNumber = (value) => /^-?\d+(\.\d+)?$/.test(String(value ?? ''));

/** Digits and one decimal point with at most two places, e.g. "1250.50". */
export const sanitizeCurrency = (raw) => {
    const [whole, ...decimals] = String(raw ?? '').replace(/[^\d.]/g, '').split('.');
    return decimals.length ? `${whole}.${decimals.join('').slice(0, 2)}` : whole;
};

export const isValidCurrency = (value) => /^\d+(\.\d{1,2})?$/.test(String(value ?? ''));

const INPUT_SETUP = {
    phone: { inputmode: 'tel', autocomplete: 'tel', format: formatPhoneForDisplay },
    number: { inputmode: 'decimal', format: sanitizeNumber },
    currency: { inputmode: 'decimal', format: sanitizeCurrency },
};

// The client form sends the first contact type when the admin picked none in
// the builder, so that dropdown keeps the browser's first-option default.
const KEEP_FIRST_OPTION = new Set(['contact.contact_type_id']);

/**
 * Show a single dropdown's placeholder as a hidden, disabled first option.
 * A fresh <select> reports its first option as selected even though nobody
 * chose it, so the placeholder is selected unless an option is marked as the
 * default (the `selected` attribute). Leaves a dropdown without a placeholder
 * alone.
 */
export const enhanceSelectPlaceholders = (
    container,
    { selector = '.field-preview select:not([multiple])' } = {}
) => {
    if (!container) return;

    container.querySelectorAll(selector).forEach((select) => {
        if (KEEP_FIRST_OPTION.has(select.name)) return;

        const placeholder = select.getAttribute('placeholder') || select.dataset.placeholder || '';
        if (!placeholder) return;

        let placeholderOption = Array.from(select.options).find(
            (opt) => opt.dataset.placeholderOption === 'true'
        );

        if (!placeholderOption) {
            const hasDefault = Array.from(select.options).some((opt) => opt.defaultSelected);

            placeholderOption = document.createElement('option');
            placeholderOption.value = '';
            placeholderOption.disabled = true;
            placeholderOption.hidden = true;
            placeholderOption.dataset.placeholderOption = 'true';
            select.insertBefore(placeholderOption, select.firstChild);

            if (!hasDefault) placeholderOption.selected = true;
        }

        // Only write when it changed: the builder re-runs this from a
        // MutationObserver, so every write would trigger another run.
        if (placeholderOption.textContent !== placeholder) {
            placeholderOption.textContent = placeholder;
        }
    });
};

/**
 * Give each phone, number and currency box on a rendered form its keyboard and
 * input filter, and tag it with data-input-kind so the submit step can check it.
 * Number boxes become text boxes: a native number input accepts "e" and
 * silently reports an empty value for anything it can't parse.
 */
export const applyInputKinds = (container, kinds) => {
    if (!container || !kinds) return;

    Object.entries(kinds).forEach(([name, kind]) => {
        const setup = INPUT_SETUP[kind];
        if (!setup) return;

        container.querySelectorAll(`input[name="${CSS.escape(name)}"]`).forEach((input) => {
            if (input.dataset.inputKind) return;
            input.dataset.inputKind = kind;

            if (input.type === 'number') input.type = 'text';
            input.setAttribute('inputmode', setup.inputmode);
            if (setup.autocomplete) input.setAttribute('autocomplete', setup.autocomplete);

            const handler = () => {
                const next = setup.format(input.value);
                if (input.value !== next) input.value = next;
            };

            input.addEventListener('input', handler);
            input.addEventListener('blur', handler);
            handler();
        });
    });
};
